import { JournalEntryTransaction } from "components/JournalEntry/AddJournalEntryModal";
import {
  ACCOUNTING_METHODS,
  ACCOUNTING_METHODS_OPTIONS,
} from "constants/accountMethod";
import { LT_SERIAL_NUMBER_PREFIX } from "constants/journalEntrySources";
import randomBytes from "randombytes";

/**
 * Transaction types for better type safety
 */
export type TransactionWithCategories = {
  category?: {
    name: string;
    uuid: string;
  };
} & Pick<
  JournalEntryTransaction,
  "cash_category_id" | "accrual_category_id" | "entry_type"
>;

export type ApiTransactionWithCategories = {
  transaction: TransactionWithCategories;
};

/**
 * Check if a transaction is a cash-only transaction
 */
export const isCashOnlyTransaction = (
  transaction: TransactionWithCategories
): boolean => {
  return (
    Boolean(transaction.cash_category_id && !transaction.accrual_category_id) ||
    transaction.entry_type === ACCOUNTING_METHODS.CASH
  );
};

/**
 * Check if a transaction is an accrual-only transaction
 */
export const isAccrualOnlyTransaction = (
  transaction: TransactionWithCategories
): boolean => {
  return Boolean(
    transaction.accrual_category_id ||
      transaction.entry_type === ACCOUNTING_METHODS.ACCRUAL
  );
};

/**
 * Filter transactions to get cash-only entries
 */
export const getCashEntries = <T extends TransactionWithCategories>(
  transactions: T[]
): T[] => {
  return transactions.filter(isCashOnlyTransaction);
};

/**
 * Filter transactions to get accrual-only entries
 */
export const getAccrualEntries = <T extends TransactionWithCategories>(
  transactions: T[]
): T[] => {
  return transactions.filter(isAccrualOnlyTransaction);
};

/**
 * Get the appropriate category ID based on whether it's a cash transaction
 */
export const getCategoryId = (
  transaction: TransactionWithCategories,
  isCashTransaction: boolean
): string | null => {
  return isCashTransaction
    ? transaction.cash_category_id || null
    : transaction.accrual_category_id || null;
};

/**
 * Get the appropriate category field name for form validation
 */
export const getCategoryFieldName = (
  transactionIndex: number | string,
  isCashTransaction: boolean
): string => {
  return isCashTransaction
    ? `transactions[${transactionIndex}].cash_category_id`
    : `transactions[${transactionIndex}].accrual_category_id`;
};

/**
 * Get the newly created accrual entries
 */
export const getNewAccrualEntries = (
  transactions: JournalEntryTransaction[]
): JournalEntryTransaction[] => {
  return getAccrualEntries(transactions).filter((txn) =>
    txn.uuid.includes("NEW_ENTRY")
  );
};

/**
 * Get the newly created cash entries
 */
export const getNewCashEntries = (
  transactions: JournalEntryTransaction[]
): JournalEntryTransaction[] => {
  return getCashEntries(transactions).filter((txn) =>
    txn.uuid.includes("NEW_ENTRY")
  );
};

/**
 * Maybe copy accrual transactions to cash transactions
 */
export const maybeCopyAccrualToCash = (
  transactions: JournalEntryTransaction[],
  isCashAndAccrualSame: boolean
) => {
  if (!isCashAndAccrualSame) {
    return transactions;
  }

  const accrualTransactions = getAccrualEntries(transactions);

  const cashTransactions = accrualTransactions.map((transaction) => ({
    ...transaction,
    id: `${randomBytes(10).toString("hex")}NEW_ENTRY`,
    uuid: `${randomBytes(10).toString("hex")}NEW_ENTRY`,
    cash_category_id: transaction.accrual_category_id,
    accrual_category_id: "",
    entry_type: ACCOUNTING_METHODS.CASH,
  }));

  return [...accrualTransactions, ...cashTransactions];
};

export const getNewJournalEntryId = () =>
  `${randomBytes(10).toString("hex")}NEW_ENTRY`;

/**
 * recalculate serial numbers for new journal entry transactions
 */
export const recalculateSerialNumbers = (
  transactions: JournalEntryTransaction[],
  maxSerialNumber: string
): JournalEntryTransaction[] => {
  const baseNumber = parseInt(maxSerialNumber.match(/\d+/)?.[0] || "100", 10);
  const prefix =
    maxSerialNumber.match(/[A-Za-z]+/)?.[0] || `${LT_SERIAL_NUMBER_PREFIX}`;

  const newEntries = transactions.filter((txn) => txn.id.includes("NEW_ENTRY"));
  const updatedNewEntries = newEntries.map((entry, index) => ({
    ...entry,
    ledger_entry_serial_number: `${prefix}${baseNumber + index + 1}`,
  }));

  return transactions.map((txn) =>
    txn.id.includes("NEW_ENTRY")
      ? updatedNewEntries[newEntries.findIndex((entry) => entry.id === txn.id)]
      : txn
  );
};

/**
 * Get the maximum existing serial number from transactions
 */
export const getMaxExistingSerialNumber = (
  transactions: JournalEntryTransaction[]
): string => {
  if (!transactions || transactions.length === 0) {
    return `${LT_SERIAL_NUMBER_PREFIX}100`;
  }

  let maxSerialNumber = 100;

  transactions.forEach((txn) => {
    if (txn.ledger_entry_serial_number) {
      const match = txn.ledger_entry_serial_number.match(/LT(\d+)/);
      if (match) {
        const serialNum = parseInt(match[1], 10);
        if (!isNaN(serialNum) && serialNum > maxSerialNumber) {
          maxSerialNumber = serialNum;
        }
      }
    }
  });

  return `${LT_SERIAL_NUMBER_PREFIX}${maxSerialNumber}`;
};
